import { PencilIcon } from '@heroicons/react/24/outline';
import React from 'react';

import {
  DiscriminatedPoliceReportUpdatedEvent,
  OfficerDetail,
  ReadPoliceReport,
} from '@/services/api/generated/matterChangesService';

import {
  ActivityBody,
  ActivityDescription,
  ActivityIcon,
  ActivityItem,
  CollapsibleList,
  FieldsChanged,
} from '../activity-feed-v2';
import { getUserFromEvent } from '../activity-feed-v2/ActivityDiffList';
import { formatEnumValue, formatFieldName } from '../activity-feed-v2/utils';

export interface PoliceReportUpdatedEventV2Props {
  event: DiscriminatedPoliceReportUpdatedEvent;
}

/**
 * V2 renderer for Police Report Updated events that uses the `previous` field
 * to show only what changed, instead of parsing the diff format.
 */
export const PoliceReportUpdatedEventV2: React.FC<
  PoliceReportUpdatedEventV2Props
> = ({ event }) => {
  const { police_report, previous } = event.payload;
  const user = getUserFromEvent(event);

  const icon = (
    <ActivityIcon
      icon={
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100">
          <PencilIcon className="h-4 w-4 text-blue-600" />
        </div>
      }
    />
  );

  // Custom field renderer for police report-specific fields
  const renderField = (
    name: keyof ReadPoliceReport,
    value: ReadPoliceReport[keyof ReadPoliceReport],
  ) => {
    const label = formatFieldName(String(name));

    // Involved officers array - show details
    if (name === 'involved_officers' && Array.isArray(value)) {
      const officers = value as OfficerDetail[];
      if (officers.length === 0) return null;

      return (
        <div>
          <ActivityDescription>
            <span className="font-medium">{label}:</span>
          </ActivityDescription>
          <CollapsibleList
            items={officers.map((officer, idx) => {
              const details: string[] = [];
              if (officer.name) details.push(`Name: ${officer.name}`);
              if (officer.badge_number)
                details.push(`Badge: ${officer.badge_number}`);
              if (officer.context) details.push(`Context: ${officer.context}`);

              return (
                <div key={idx} className="space-y-0.5">
                  {details.map((d, i) => (
                    <div key={i}>{d}</div>
                  ))}
                </div>
              );
            })}
            showLabel={`Show Officers (${officers.length})`}
            hideLabel="Hide Officers"
            defaultExpanded={officers.length <= 2}
          />
        </div>
      );
    }

    // Police department object - show name
    if (name === 'police_department' && value && typeof value === 'object') {
      const department = value as Record<string, unknown>;
      return (
        <ActivityDescription>
          <span className="font-medium">{label}:</span>{' '}
          {department.name ? String(department.name) : 'N/A'}
        </ActivityDescription>
      );
    }

    // Date fields - format nicely
    if (
      (name === 'reported_incident_date' || name === 'availability_estimate') &&
      value &&
      typeof value === 'string'
    ) {
      return (
        <ActivityDescription>
          <span className="font-medium">{label}:</span>{' '}
          {new Date(value).toLocaleDateString()}
        </ActivityDescription>
      );
    }

    // Enum fields - format nicely
    if (
      name === 'attestation_status' ||
      name === 'availability_status' ||
      name === 'escalation_reason'
    ) {
      return (
        <ActivityDescription>
          <span className="font-medium">{label}:</span>{' '}
          {formatEnumValue(String(value))}
        </ActivityDescription>
      );
    }

    // Long text fields - show in collapsible if long
    if (
      (name === 'attestation_description' || name === 'description') &&
      value &&
      typeof value === 'string'
    ) {
      const text = value as string;
      if (text.length <= 150) {
        return (
          <ActivityDescription>
            <span className="font-medium">{label}:</span> {text}
          </ActivityDescription>
        );
      }

      return (
        <div>
          <ActivityDescription>
            <span className="font-medium">{label}:</span>
          </ActivityDescription>
          <CollapsibleList
            items={[<span key="desc">{text}</span>]}
            showLabel="Show Description"
            hideLabel="Hide Description"
          />
        </div>
      );
    }

    // Default rendering for simple fields
    return (
      <ActivityDescription>
        <span className="font-medium">{label}:</span> {String(value)}
      </ActivityDescription>
    );
  };

  const body = (
    <ActivityBody>
      <FieldsChanged<ReadPoliceReport>
        previous={previous}
        current={police_report}
        fieldsToCheck={[
          'attestation_status',
          'attestation_description',
          'availability_status',
          'availability_estimate',
          'description',
          'report_identifier',
          'reported_incident_date',
          'reported_location',
          'involved_officers',
          'escalation_reason',
        ]}
        renderField={renderField}
      />
    </ActivityBody>
  );

  return (
    <ActivityItem
      icon={icon}
      title="Police Report Updated"
      timestamp={new Date(event.occurred_at ?? new Date())}
      user={user}
      body={body}
    />
  );
};
